<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Offer questions</h4>
      <router-link :to="{ name: 'questionnaire-flows' }" class="btn btn-secondary">Questionnaire flows</router-link>
      <router-link :to="{ name: 'offer-questions-create' }" class="btn btn-primary create-btn">Create</router-link>
    </div>

    <card>
      <div class="oq-filters">
        <el-select v-model="filterKey" placeholder="Filter key" clearable style="width: 180px" class="mr-2">
          <el-option v-for="k in filterKeys" :key="k" :label="k" :value="k" />
        </el-select>
        <el-select
          v-if="filterKey === 'tag'"
          v-model="filterValue"
          placeholder="Tag"
          clearable
          allow-create
          filterable
          style="width: 200px"
          class="mr-2"
        >
          <el-option v-for="t in tagValues" :key="t" :label="t" :value="t" />
        </el-select>
        <el-input
          v-else
          v-model="filterValue"
          clearable
          placeholder="Filter value (id / kind)"
          style="width: 200px"
          class="mr-2"
          @keyup.enter.native="applyFilters"
        />
        <el-select v-model="status" placeholder="Status" clearable style="width: 140px" class="mr-2">
          <el-option v-for="s in statuses" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
        <el-input
          v-model="idQuery"
          clearable
          placeholder="ID"
          style="width: 220px"
          class="mr-2"
          @keyup.enter.native="applyFilters"
        />
        <el-button type="primary" class="mr-2" @click="applyFilters">Filter</el-button>
        <el-button @click="clearFilters">Clear</el-button>
      </div>

      <el-table :data="questionsList" v-loading="loading" style="width: 100%">
        <el-table-column label="ID" prop="id" min-width="200" />
        <el-table-column label="Title (EN)" min-width="260">
          <template slot-scope="{ row }">
            <span>{{ tr(row.title) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Filters" min-width="220">
          <template slot-scope="{ row }">
            <el-tag
              v-for="(f, i) in (row.filters || [])"
              :key="i"
              size="mini"
              class="oq-tag"
              :type="f.key === 'tag' ? 'warning' : 'info'"
            >{{ f.key }}: {{ f.value }}</el-tag>
            <span v-if="!(row.filters && row.filters.length)">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Answers" width="90" align="center">
          <template slot-scope="{ row }">{{ (row.answers || []).length }}</template>
        </el-table-column>
        <el-table-column label="Status" width="110">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.status === 'active' ? 'success' : 'info'">{{ row.status || '—' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Weight" prop="weight" width="90" align="center" />
        <el-table-column fixed="right" label="Edit" width="80" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="editQuestion(row)">Edit</el-button>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Delete" width="80" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" type="danger" @click="handleDelete(row)">Del</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="d-flex justify-content-end mt-3">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="pageSizes"
          :page-size.sync="perPage"
          :current-page.sync="page"
          :total="questionsTotal"
          @current-change="loadList"
          @size-change="applyFilters"
        />
      </div>
    </card>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import APIOfferQuestions from "@/api/modules/offer-questions";

// Filter keys / tag values observed in the legacy monolith question calls.
const FILTER_KEYS = ["illnesses.id", "illnesses.kind", "operations.id", "diagnostics.id", "tag"];
const TAG_VALUES = ["general", "general_illness", "general_procedure"];
const STATUSES = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default {
  data() {
    return {
      loading: false,
      filterKey: "",
      filterValue: "",
      status: "",
      idQuery: "",
      page: 1,
      perPage: 50,
      pageSizes: [50, 100, 200],
      filterKeys: FILTER_KEYS,
      tagValues: TAG_VALUES,
      statuses: STATUSES,
    };
  },
  computed: {
    ...mapState("offerQuestions", ["questionsList", "questionsTotal"]),
  },
  methods: {
    ...mapActions("offerQuestions", ["getQuestions", "deleteQuestion"]),
    tr(field) {
      if (!field) return "—";
      return field.en || field.ru || Object.values(field)[0] || "—";
    },
    buildParams() {
      // Yii2 pagination is 1-based.
      const params = { page: this.page, "per-page": this.perPage };
      if (this.filterKey && this.filterValue) {
        params["filters[0][key]"] = this.filterKey;
        params["filters[0][value]"] = this.filterValue;
      }
      if (this.status) params.status = this.status;
      return params;
    },
    async loadList() {
      if (this.idQuery && this.idQuery.trim()) {
        return this.findById(this.idQuery.trim());
      }
      this.loading = true;
      try {
        await this.getQuestions(this.buildParams());
      } finally {
        this.loading = false;
      }
    },
    async findById(id) {
      // GET /admin/questions/{id} works directly (unlike the ignored ?id= param).
      this.loading = true;
      try {
        const resp = await APIOfferQuestions.getQuestion(id);
        const q = (resp && resp.data && (resp.data.question || resp.data)) || null;
        const found = q && q.id ? [q] : [];
        this.$store.commit("offerQuestions/SET_DATA", { type: "questionsList", data: found });
        this.$store.commit("offerQuestions/SET_DATA", { type: "questionsTotal", data: found.length });
        if (!found.length) this.$notify({ type: "warning", title: `Question ${id} not found` });
      } catch (e) {
        this.$store.commit("offerQuestions/SET_DATA", { type: "questionsList", data: [] });
        this.$store.commit("offerQuestions/SET_DATA", { type: "questionsTotal", data: 0 });
        this.$notify({ type: "warning", title: `Question ${id} not found` });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.loadList();
    },
    clearFilters() {
      this.filterKey = "";
      this.filterValue = "";
      this.status = "";
      this.idQuery = "";
      this.page = 1;
      this.loadList();
    },
    editQuestion(row) {
      // Stash the already-loaded row so the edit form can render instantly; the
      // form re-fetches via GET /{id} as well.
      this.$store.commit("offerQuestions/SET_DATA", { type: "question", data: row });
      this.$router.push({ name: "offer-questions-edit", params: { id: row.id } });
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`Delete question "${this.tr(row.title)}"?`, "Warning", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        this.loading = true;
        await this.deleteQuestion(row.id);
        this.$notify({ type: "success", title: "Question deleted" });
        await this.loadList();
      } catch (e) {
        if (e !== "cancel") {
          this.$notify({ type: "warning", title: "Error deleting question" });
        }
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.loadList();
  },
};
</script>

<style scoped>
.oq-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 0;
  margin-bottom: 16px;
}
.oq-tag {
  margin: 0 4px 4px 0;
}
.create-btn {
  height: 40px;
  display: inline-flex;
  align-items: center;
}
.el-select {
  margin-bottom: 0;
}
</style>
